import React, { useEffect, useMemo, useState } from 'react';

import { MergeFieldRegistryEntry } from '~forms/types';

import { fetchFormImageObjectUrl } from './formImageResolver';
import { FormImageMap, renderRichText } from './renderRichText';
import { extractFormImageRefs, isRichTextEmpty } from './sanitizeRichText';

interface ComponentProps {
  html: string;
  // Merge-field registry — lets pasted {{merge:<token>}} sentinels render as the shared blue chip.
  registry?: MergeFieldRegistryEntry[];
  className?: string;
  ariaLabel?: string;
  // Text shown when there is no content (canvas/editor want a prompt; the filler shows nothing).
  emptyText?: string;
}

/**
 * Read-only render of rich-text content: sanitises the stored HTML and displays it, turning inline
 * merge-field sentinels into chips. This is the "view only, same formatting" surface the filler sees
 * (e.g. T&Cs). Shared by every read-only rich-text surface (canvas preview, fill page, frozen
 * completed-form record), so it's also where every `data-form-image` reference gets resolved to an
 * authenticated blob object URL (TAV-3155).
 */
const RichTextView: React.FC<ComponentProps> = ({ html, registry, className, ariaLabel, emptyText }) => {
  const refs = useMemo(() => extractFormImageRefs(html), [html]);
  const [images, setImages] = useState<FormImageMap>({});

  // Resolves every image reference in `html` to a blob object URL. `refs` is memoised on `html`, so
  // its identity is stable while the HTML is stable and changes exactly when the HTML does, and that
  // gives correct revocation both on unmount and on HTML change with a single effect.
  useEffect(() => {
    if (refs.length === 0) {
      setImages({});
      return undefined;
    }

    let cancelled = false;
    const created: string[] = [];

    Promise.all(
      refs.map(async (ref) => {
        try {
          return [ref, await fetchFormImageObjectUrl(ref)] as const;
        } catch {
          // A failed fetch leaves the placeholder in place. No toast: this is a read-only display
          // surface that can be mounted four different ways, and a per-image error banner on a
          // frozen record is noise, not an action the reader can take.
          return null;
        }
      }),
    ).then((entries) => {
      const next: FormImageMap = {};
      entries.forEach((entry) => {
        if (!entry) return;
        next[entry[0]] = entry[1];
        created.push(entry[1]);
      });
      // The effect may have been torn down while the fetches were in flight. Revoke here rather
      // than leaking, because cleanup already ran and will not run again for these URLs.
      if (cancelled) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      setImages(next);
    });

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [refs]);

  const content = useMemo(() => renderRichText(html, registry, images), [html, registry, images]);
  const empty = useMemo(() => isRichTextEmpty(html), [html]);

  const classes = ['rich-text-view', className].filter(Boolean).join(' ');

  if (empty) {
    if (!emptyText) return null;
    return (
      <div className={`${classes} rich-text-view--empty`} aria-label={ariaLabel}>
        <span className="text-muted">{emptyText}</span>
      </div>
    );
  }

  return (
    <div className={classes} aria-label={ariaLabel}>
      {content}
    </div>
  );
};

export default RichTextView;
